import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './SelectionHeader.styled';
import { SelectionHeaderProps, Action } from './SelectionHeader.types';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import { Button } from '@/components/UI/Button/Button';
import { Portal } from '@/components/UI/Portal/Portal';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { useBookmarkSelectionStore } from '@/features/BookmarkCollections/store';
import { ConfirmDeleteModal } from '@/features/BookmarkCollections/components/ConfirmDeleteModal';

export const SelectionHeader = ({
  title = '',
  actions = {},
}: SelectionHeaderProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useBreakpoint();
  const {
    isSelectionActive,
    selectedItems,
    setIsSelectionActive,
    clearSelection,
  } = useBookmarkSelectionStore();

  const headerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const [isFixed, setIsFixed] = useState(false);
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<Action | null>(null);

  const primaryActionAppearance = actions.primary?.appearance ?? 'primary';
  const secondaryActionAppearance =
    actions.secondary?.appearance ?? 'secondary';

  const selectedCount = selectedItems.length;
  const selectionTitle =
    selectedCount > 0
      ? t('bookmark_collections.selected_items_count', { count: selectedCount })
      : t('bookmark_collections.select_items');

  const handleDeselection = () => {
    setIsSelectionActive(false);
    clearSelection();
  };

  const handleActionClick = () => {
    if (actions.primary?.confirm) {
      setConfirmAction(actions.primary);
    } else {
      actions.primary?.onClick();
      setIsActionSheetOpen(false);
    }
  };

  const handleSecondaryActionClick = () => {
    if (actions.secondary?.confirm) {
      setConfirmAction(actions.secondary);
    } else {
      actions.secondary?.onClick();
    }
  };

  const ConfirmActionModal = useMemo(() => {
    return (
      <Portal>
        <ConfirmDeleteModal
          title={confirmAction?.confirm?.title}
          description={confirmAction?.confirm?.description}
          isOpen={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          onSubmit={() => {
            confirmAction?.onClick();
            setConfirmAction(null);
          }}
        />
      </Portal>
    );
  }, [confirmAction]);

  useEffect(() => {
    const trigger = triggerRef.current;
    const headerElement = headerRef.current?.offsetHeight;

    if (!isSelectionActive || !trigger || !headerElement) {
      setIsFixed(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFixed(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(trigger);

    return () => {
      observer.unobserve(trigger);
    };
  }, [isSelectionActive]);

  useEffect(() => {
    setIsActionSheetOpen(isSelectionActive);
  }, [isSelectionActive]);

  if (!isDesktop && isSelectionActive) {
    return (
      <>
        <S.MobileHeader>
          <S.CloseButton
            onClick={handleDeselection}
            data-testid="deselect-button"
          >
            <CloseIcon width={15} height={15} />
          </S.CloseButton>
          <S.SelectionTitle>{selectionTitle}</S.SelectionTitle>
        </S.MobileHeader>

        {confirmAction && ConfirmActionModal}

        <Portal>
          <BottomSheet
            hasOverlay={false}
            isOpen={isActionSheetOpen}
            close={handleDeselection}
          >
            <S.MobileActionSheet>
              <S.MobileActionsWrapper
                $hasPriority={!!actions.primary?.priority}
              >
                <S.SecondaryActionButton
                  onClick={handleSecondaryActionClick}
                  disabled={!selectedCount}
                  appearance={secondaryActionAppearance}
                >
                  {actions.secondary?.title}
                </S.SecondaryActionButton>
                <S.MobilePrimaryActionButton
                  disabled={!selectedCount}
                  appearance={primaryActionAppearance}
                  onClick={handleActionClick}
                  data-testid="selection-action-button"
                >
                  {actions.primary?.title}
                </S.MobilePrimaryActionButton>
              </S.MobileActionsWrapper>
            </S.MobileActionSheet>
          </BottomSheet>
        </Portal>
      </>
    );
  }

  if (isDesktop) {
    return (
      <>
        {isSelectionActive && (
          <>
            <div ref={triggerRef} style={{ height: '1px' }} />
            <S.HeaderPlaceholder
              $isFixed={isFixed}
              $height={headerRef.current?.offsetHeight ?? 0}
            />
          </>
        )}

        <S.HeaderBox ref={headerRef} $isFixed={isFixed}>
          <S.Header $isSelectionMode={isSelectionActive}>
            {isSelectionActive ? (
              <S.TitleWrapper>
                <S.CloseButton onClick={handleDeselection}>
                  <CloseIcon width={16} height={16} />
                </S.CloseButton>
                <S.SelectionTitle>{selectionTitle}</S.SelectionTitle>
              </S.TitleWrapper>
            ) : (
              <S.Title>{title}</S.Title>
            )}

            {isSelectionActive && (
              <S.ActionsWrapper $hasPriority={!!actions.primary?.priority}>
                <S.SecondaryActionButton
                  onClick={handleSecondaryActionClick}
                  disabled={!selectedCount}
                  appearance={secondaryActionAppearance}
                >
                  {actions.secondary?.title}
                </S.SecondaryActionButton>

                <S.ActionButton
                  onClick={handleActionClick}
                  disabled={!selectedCount}
                  appearance={primaryActionAppearance}
                >
                  {actions.primary?.title}
                </S.ActionButton>
              </S.ActionsWrapper>
            )}

            {confirmAction && ConfirmActionModal}
          </S.Header>
        </S.HeaderBox>
      </>
    );
  }
};
